import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import { toast } from 'sonner';

// Which tokens each group uses (read from its wording template).
const tokensOf = (g) => ((g?.template || '') + ' ' + (g?.template_reroof || '')).match(/\{(\w+)\}/g)?.map(t => t.slice(1, -1))
  .filter(t => !['roof_type', 'paint_items', 'safety_items', 'service_name'].includes(t)) || [];

const STATUS = { confirmed: 'bg-emerald-100 text-emerald-800', prefilled: 'bg-amber-100 text-amber-800', blank: 'bg-rose-100 text-rose-800' };

const Row = ({ r, groups, byCode, patch }) => {
  const toks = [...new Set(tokensOf(byCode[r.bullet_group]))];
  const [vals, setVals] = useState(r.bullet_tokens || {});
  return (
    <tr className="border-t" data-testid={`bullet-cat-row-${r.id}`}>
      <td className="px-2 py-1 text-[11px] text-slate-500">{r.component}</td>
      <td className="px-2 py-1 text-xs">{r.name}</td>
      <td className="px-2 py-1">
        <select data-testid={`bullet-cat-group-${r.id}`} value={r.bullet_group}
          onChange={e => patch(r, { bullet_group: e.target.value, bullet_tokens: vals })}
          className="border border-slate-200 rounded text-xs px-1 py-0.5 max-w-[220px]">
          <option value="">— not printed —</option>
          {groups.map(g => <option key={g.code} value={g.code}>{g.code} · {g.title}</option>)}
        </select>
      </td>
      <td className="px-2 py-1">
        <div className="flex flex-wrap gap-1">
          {toks.length === 0 && <span className="text-[11px] text-slate-400">—</span>}
          {toks.map(t => (
            <label key={t} className="text-[10px] text-slate-500 flex items-center gap-1">{t}
              <input data-testid={`bullet-cat-token-${r.id}-${t}`} value={vals[t] || ''} onChange={e => setVals({ ...vals, [t]: e.target.value })}
                onBlur={() => (vals[t] || '') !== ((r.bullet_tokens || {})[t] || '') && patch(r, { bullet_tokens: vals })}
                className="border border-slate-200 rounded px-1 py-0.5 text-xs w-28" />
            </label>
          ))}
        </div>
      </td>
      <td className="px-2 py-1">
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS[r.status]}`} data-testid={`bullet-cat-status-${r.id}`}>{r.status}</span>
      </td>
      <td className="px-2 py-1">
        {r.status !== 'confirmed' && (
          <button data-testid={`bullet-cat-confirm-${r.id}`} onClick={() => patch(r, { bullet_group: r.bullet_group, bullet_tokens: vals })}
            className="p-1 text-emerald-700 hover:bg-emerald-50 rounded" title="Looks right — confirm"><Check size={13} /></button>
        )}
      </td>
    </tr>
  );
};

export const CatalogueTab = ({ api, groups }) => {
  const [data, setData] = useState({ rows: [], stats: {}, components: [] });
  const [component, setComponent] = useState('ridge');
  const [status, setStatus] = useState('');
  const byCode = useMemo(() => Object.fromEntries(groups.map(g => [g.code, g])), [groups]);
  const load = useCallback(async () => {
    try { setData((await api.get('/bullet-groups/catalogue', { params: { component } })).data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load'); }
  }, [api, component]);
  useEffect(() => { load(); }, [load]);
  const patch = async (r, body) => {
    try {
      const { data: d } = await api.patch(`/bullet-groups/catalogue/${r.id}`, body);
      setData(s => {
        const rows = s.rows.map(x => (x.id === r.id ? { ...x, ...d } : x));
        const stats = { ...s.stats };
        if (r.status !== d.status) { stats[r.status] = Math.max(0, (stats[r.status] || 0) - 1); stats[d.status] = (stats[d.status] || 0) + 1; }
        return { ...s, rows, stats };
      });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const rows = data.rows.filter(r => !status || r.status === status);
  return (
    <div className="space-y-3" data-testid="bullet-catalogue-tab">
      <div className="flex flex-wrap items-center gap-2">
        <select data-testid="bullet-cat-component" value={component} onChange={e => setComponent(e.target.value)} className="border border-slate-200 rounded px-2 py-1 text-sm">
          {data.components.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="bullet-cat-status-filter" value={status} onChange={e => setStatus(e.target.value)} className="border border-slate-200 rounded px-2 py-1 text-sm">
          <option value="">All</option><option value="prefilled">Pre-filled (check)</option><option value="confirmed">Confirmed</option><option value="blank">Blank</option>
        </select>
        <span className="text-xs text-slate-500" data-testid="bullet-cat-stats">
          {data.stats.confirmed || 0} confirmed · {data.stats.prefilled || 0} pre-filled · {data.stats.blank || 0} blank
        </span>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr><th className="px-2 py-2">Component</th><th className="px-2 py-2">Catalogue item</th><th className="px-2 py-2">Bullet group</th>
              <th className="px-2 py-2">Token values</th><th className="px-2 py-2">Status</th><th /></tr>
          </thead>
          <tbody>{rows.map(r => <Row key={`${r.id}-${r.bullet_group}`} r={r} groups={groups} byCode={byCode} patch={patch} />)}</tbody>
        </table>
      </div>
    </div>
  );
};
